import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { useContext, useEffect } from "react";
import z from "zod";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTypeAccessEditAllowed } from "@/hooks/use-type-access-edit-allowed";
import {
	useDisableJobType,
	useEnableJobType,
	useUpdateJobType,
} from "@/services/job/job.mutations";
import { jobQueryOptions } from "@/services/job/job.options";
import { JobTypeDrawerContext } from "@/utils/job-type-detail/job-type-drawer-context";
import { showSuccessToast } from "@/utils/toast.utils";
import type { OrgJobTypeFullPageTab } from "./OrgJobTypeFullPage";
import { OrgJobTypeFullPage } from "./OrgJobTypeFullPage";

const SearchSchema = z.object({
	tab: z
		.enum(["details", "access", "form", "lifecycle", "rules", "activity"])
		.optional()
		.default("details"),
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z.enum(["drawer", "modal", "full"]).optional().default("full"),
	ruleId: z.string().optional(),
	/** Editor open mode for the rule drawer on `/full/form/rules`. */
	ruleMode: z.enum(["create", "edit", "delete"]).optional(),
	/** Sub-tab inside a single rule's drawer/full view. Currently unused on form rules. */
	ruleTab: z.enum(["details", "activity"]).optional(),
});

const FULL_PARENT =
	"/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full" as const;

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full",
)({
	component: OrgJobTypeFullView,
	validateSearch: (search) => SearchSchema.parse(search),
	params: {
		parse: (raw) => {
			const p = raw as { orgId: string; jobTypeId: string };
			return {
				orgId: z.string().parse(p.orgId),
				jobTypeId: z.string().parse(p.jobTypeId),
			};
		},
	},
});

function OrgJobTypeFullView() {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/jobTypes/$jobTypeId/full",
	});
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const organizationIdForApi = params.orgId;

	const detailQuery = useQuery(
		jobQueryOptions.jobTypeDetail(params.jobTypeId, {
			organizationId: organizationIdForApi,
		}),
	);
	const jobType = detailQuery.data ?? null;
	const drawerCtx = useContext(JobTypeDrawerContext);
	const accessEdit = useTypeAccessEditAllowed("JobType", params.jobTypeId);
	const updateMutation = useUpdateJobType();
	const enableMutation = useEnableJobType();
	const disableMutation = useDisableJobType();
	const isSavingAny =
		updateMutation.isPending ||
		enableMutation.isPending ||
		disableMutation.isPending;

	const isEditMode =
		search.mode === "edit" &&
		(search.tab === "access"
			? accessEdit.allowed
			: (drawerCtx?.canEdit ?? false));

	useEffect(() => {
		if (search.mode !== "edit" || search.tab !== "access") return;
		if (accessEdit.isPending || accessEdit.allowed) return;
		void navigate({
			to: FULL_PARENT,
			params: { orgId: params.orgId, jobTypeId: params.jobTypeId },
			search: {
				tab: search.tab,
				mode: "view",
				presentation: search.presentation,
			},
		});
	}, [
		accessEdit.allowed,
		accessEdit.isPending,
		navigate,
		params.jobTypeId,
		params.orgId,
		search.mode,
		search.presentation,
		search.tab,
	]);

	const formSegment = `/configuration/jobTypes/${params.jobTypeId}/full/form`;
	const onFormRoutes = pathname.includes(formSegment);
	const rulesSegment = `/configuration/jobTypes/${params.jobTypeId}/full/rules/`;
	const onRuleDetailRoute = pathname.includes(rulesSegment);

	const activeTab: OrgJobTypeFullPageTab = onFormRoutes ? "form" : search.tab;

	if (onRuleDetailRoute || onFormRoutes) {
		return <Outlet />;
	}

	const handleTabChange = (tab: OrgJobTypeFullPageTab) => {
		if (tab === "form") {
			void navigate({
				to: FULL_PARENT,
				params: { orgId: params.orgId, jobTypeId: params.jobTypeId },
				search: {
					tab: "form",
					mode: search.mode,
					presentation: search.presentation,
				},
			});
			return;
		}
		void navigate({
			to: FULL_PARENT,
			params: { orgId: params.orgId, jobTypeId: params.jobTypeId },
			search: { tab, mode: search.mode, presentation: search.presentation },
		});
	};

	const handleBackToList = () => {
		void navigate({
			to: "/org-admin/$orgId/configuration/jobTypes",
			params: { orgId: params.orgId },
		});
	};

	if (!jobType && !detailQuery.isLoading && !detailQuery.isError) {
		return null;
	}

	if (!jobType) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-sm text-gray-500">
					{detailQuery.isLoading
						? t("common.actions.loadingEllipsis")
						: t("admin.jobTypes.drawer.errors.notFound")}
				</div>
			</div>
		);
	}

	const fullPageContext = drawerCtx
		? {
				...drawerCtx,
				mode: (search.mode ?? "view") as "view" | "edit",
				setMode: (mode: "view" | "edit") => {
					void navigate({
						to: FULL_PARENT,
						params: { orgId: params.orgId, jobTypeId: params.jobTypeId },
						search: {
							tab: search.tab,
							mode,
							presentation: search.presentation,
						},
					});
				},
				isSaving: isSavingAny,
				saveDraft: async () => {
					if (!drawerCtx.isValid || isSavingAny) return;
					if (
						search.tab === "access"
							? !accessEdit.allowed
							: !drawerCtx.canEdit
					)
						return;
					try {
						if (search.tab === "access") {
							if (drawerCtx.draftSubOrgIds === null) {
								await enableMutation.mutateAsync({
									jobTypeId: params.jobTypeId,
									organizationId: organizationIdForApi,
								});
							} else if (drawerCtx.draftSubOrgIds.length === 0) {
								await disableMutation.mutateAsync({
									jobTypeId: params.jobTypeId,
									organizationId: organizationIdForApi,
								});
							} else {
								await updateMutation.mutateAsync({
									jobTypeId: params.jobTypeId,
									data: {
										name: drawerCtx.jobType.name?.trim() ?? "",
										description: drawerCtx.jobType.description || null,
										icon: drawerCtx.jobType.icon || null,
										typeId: params.jobTypeId,
										statusLifecycleId: drawerCtx.jobType.lifecycleId ?? null,
										subOrganizationIds: drawerCtx.draftSubOrgIds,
										locationRequired: drawerCtx.locationRequired,
									},
									organizationId: organizationIdForApi,
								});
							}
							showSuccessToast({
								message: t("admin.jobTypes.drawer.access.toast.saveSuccess"),
							});
						} else {
							const currentSubOrgIds = (jobType?.jobTypeSubOrganizations ?? [])
								.map((s) => s.id)
								.filter((id): id is string => id !== null);
							await updateMutation.mutateAsync({
								jobTypeId: params.jobTypeId,
								data: {
									name: drawerCtx.draftName.trim(),
									description: drawerCtx.draftDescription || null,
									icon: drawerCtx.draftIcon || null,
									typeId: params.jobTypeId,
									statusLifecycleId: drawerCtx.jobType.lifecycleId ?? null,
									subOrganizationIds:
										currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
									locationRequired: drawerCtx.locationRequired,
								},
								organizationId: organizationIdForApi,
							});
							showSuccessToast({
								message: t("admin.jobTypes.drawer.details.toast.saveSuccess"),
							});
						}
						void navigate({
							to: FULL_PARENT,
							params: { orgId: params.orgId, jobTypeId: params.jobTypeId },
							search: {
								tab: search.tab,
								mode: "view" as const,
								presentation: search.presentation,
							},
						});
					} catch {
						// Global mutation error handler in root-provider.tsx already shows the error toast
					}
				},
				cancelDraft: () => {
					drawerCtx.setDraftName(drawerCtx.jobType.name ?? "");
					drawerCtx.setDraftDescription(drawerCtx.jobType.description ?? "");
					drawerCtx.setDraftIcon(drawerCtx.jobType.icon ?? "");
					if (jobType) {
						const subOrgIds = (jobType.jobTypeSubOrganizations ?? [])
							.map((s) => s.id)
							.filter((id): id is string => id !== null);
						drawerCtx.setDraftSubOrgIds(
							subOrgIds.length > 0 ? subOrgIds : null,
						);
					}
					void navigate({
						to: FULL_PARENT,
						params: { orgId: params.orgId, jobTypeId: params.jobTypeId },
						search: {
							tab: search.tab,
							mode: "view" as const,
							presentation: search.presentation,
						},
					});
				},
			}
		: null;

	return (
		<JobTypeDrawerContext.Provider value={fullPageContext}>
			<OrgJobTypeFullPage
				jobType={jobType}
				orgId={params.orgId}
				activeTab={activeTab}
				onTabChange={handleTabChange}
				onBackToList={handleBackToList}
				isLoading={detailQuery.isLoading}
				isError={detailQuery.isError}
				isEditMode={isEditMode}
			>
				<Outlet />
			</OrgJobTypeFullPage>
		</JobTypeDrawerContext.Provider>
	);
}
